import { __extends } from "tslib";
import { EngineStore } from "../Engines/engineStore.js";
import { Mesh } from "../Meshes/mesh.js";
import { Observable } from "./observable.js";
/**
 * Defines the root class used to create scene optimization to use with SceneOptimizer
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var SceneOptimization = /** @class */ (function () {
    /**
     * Creates the SceneOptimization object
     * @param priority defines the priority of this optimization (0 by default which means first in the list)
     */
    function SceneOptimization(
    /**
     * Defines the priority of this optimization (0 by default which means first in the list)
     */
    priority) {
        if (priority === void 0) { priority = 0; }
        this.priority = priority;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @returns description string
     */
    SceneOptimization.prototype.getDescription = function () {
        return "";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    SceneOptimization.prototype.apply = function (scene, optimizer) {
        return true;
    };
    return SceneOptimization;
}());
export { SceneOptimization };
/**
 * Defines an optimization used to reduce the size of render target textures
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var TextureOptimization = /** @class */ (function (_super) {
    __extends(TextureOptimization, _super);
    /**
     * Creates the TextureOptimization object
     * @param priority defines the priority of this optimization (0 by default which means first in the list)
     * @param maximumSize defines the maximum sized allowed for textures (1024 is the default value). If a texture is bigger, it will be scaled down using a factor defined by the step parameter
     * @param step defines the factor (0.5 by default) used to scale down textures bigger than maximum sized allowed.
     */
    function TextureOptimization(
    /**
     * Defines the priority of this optimization (0 by default which means first in the list)
     */
    priority, 
    /**
     * Defines the maximum sized allowed for textures (1024 is the default value). If a texture is bigger, it will be scaled down using a factor defined by the step parameter
     */
    maximumSize, 
    /**
     * Defines the factor (0.5 by default) used to scale down textures bigger than maximum sized allowed.
     */
    step) {
        if (priority === void 0) { priority = 0; }
        if (maximumSize === void 0) { maximumSize = 1024; }
        if (step === void 0) { step = 0.5; }
        var _this = _super.call(this, priority) || this;
        _this.priority = priority;
        _this.maximumSize = maximumSize;
        _this.step = step;
        return _this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @returns description string
     */
    TextureOptimization.prototype.getDescription = function () {
        return "Reducing render target texture size to " + this.maximumSize;
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    TextureOptimization.prototype.apply = function (scene, optimizer) {
        var allDone = true;
        for (var index = 0; index < scene.textures.length; index++) {
            var texture = scene.textures[index];
            if (!texture.canRescale || texture.getContext) {
                continue;
            }
            var currentSize = texture.getSize();
            var maxDimension = Math.max(currentSize.width, currentSize.height);
            if (maxDimension > this.maximumSize) {
                texture.scale(this.step);
                allDone = false;
            }
        }
        return allDone;
    };
    return TextureOptimization;
}(SceneOptimization));
export { TextureOptimization };
/**
 * Defines an optimization used to increase or decrease the rendering resolution
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var HardwareScalingOptimization = /** @class */ (function (_super) {
    __extends(HardwareScalingOptimization, _super);
    /**
     * Creates the HardwareScalingOptimization object
     * @param priority defines the priority of this optimization (0 by default which means first in the list)
     * @param maximumScale defines the maximum scale to use (2 by default)
     * @param step defines the step to use between two passes (0.5 by default)
     */
    function HardwareScalingOptimization(
    /**
     * Defines the priority of this optimization (0 by default which means first in the list)
     */
    priority, 
    /**
     * Defines the maximum scale to use (2 by default)
     */
    maximumScale, 
    /**
     * Defines the step to use between two passes (0.5 by default)
     */
    step) {
        if (priority === void 0) { priority = 0; }
        if (maximumScale === void 0) { maximumScale = 2; }
        if (step === void 0) { step = 0.25; }
        var _this = _super.call(this, priority) || this;
        _this.priority = priority;
        _this.maximumScale = maximumScale;
        _this.step = step;
        _this._currentScale = -1;
        _this._directionOffset = 1;
        return _this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @return description string
     */
    HardwareScalingOptimization.prototype.getDescription = function () {
        return "Setting hardware scaling level to " + this._currentScale;
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    HardwareScalingOptimization.prototype.apply = function (scene, optimizer) {
        if (this._currentScale === -1) {
            this._currentScale = scene.getEngine().getHardwareScalingLevel();
            if (this._currentScale > this.maximumScale) {
                this._directionOffset = -1;
            }
        }
        this._currentScale += this._directionOffset * this.step;
        scene.getEngine().setHardwareScalingLevel(this._currentScale);
        return this._directionOffset === 1 ? this._currentScale >= this.maximumScale : this._currentScale <= this.maximumScale;
    };
    return HardwareScalingOptimization;
}(SceneOptimization));
export { HardwareScalingOptimization };
/**
 * Defines an optimization used to remove shadows
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var ShadowsOptimization = /** @class */ (function (_super) {
    __extends(ShadowsOptimization, _super);
    function ShadowsOptimization() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @return description string
     */
    ShadowsOptimization.prototype.getDescription = function () {
        return "Turning shadows on/off";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    ShadowsOptimization.prototype.apply = function (scene, optimizer) {
        scene.shadowsEnabled = optimizer.isInImprovementMode;
        return true;
    };
    return ShadowsOptimization;
}(SceneOptimization));
export { ShadowsOptimization };
/**
 * Defines an optimization used to turn post-processes off
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var PostProcessesOptimization = /** @class */ (function (_super) {
    __extends(PostProcessesOptimization, _super);
    function PostProcessesOptimization() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @return description string
     */
    PostProcessesOptimization.prototype.getDescription = function () {
        return "Turning post-processes on/off";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    PostProcessesOptimization.prototype.apply = function (scene, optimizer) {
        scene.postProcessesEnabled = optimizer.isInImprovementMode;
        return true;
    };
    return PostProcessesOptimization;
}(SceneOptimization));
export { PostProcessesOptimization };
/**
 * Defines an optimization used to turn lens flares off
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var LensFlaresOptimization = /** @class */ (function (_super) {
    __extends(LensFlaresOptimization, _super);
    function LensFlaresOptimization() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @return description string
     */
    LensFlaresOptimization.prototype.getDescription = function () {
        return "Turning lens flares on/off";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    LensFlaresOptimization.prototype.apply = function (scene, optimizer) {
        scene.lensFlaresEnabled = optimizer.isInImprovementMode;
        return true;
    };
    return LensFlaresOptimization;
}(SceneOptimization));
export { LensFlaresOptimization };
/**
 * Defines an optimization based on user defined callback.
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var CustomOptimization = /** @class */ (function (_super) {
    __extends(CustomOptimization, _super);
    function CustomOptimization() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @returns description string
     */
    CustomOptimization.prototype.getDescription = function () {
        if (this.onGetDescription) {
            return this.onGetDescription();
        }
        return "Running user defined callback";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    CustomOptimization.prototype.apply = function (scene, optimizer) {
        if (this.onApply) {
            return this.onApply(scene, optimizer);
        }
        return true;
    };
    return CustomOptimization;
}(SceneOptimization));
export { CustomOptimization };
/**
 * Defines an optimization used to turn particles off
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var ParticlesOptimization = /** @class */ (function (_super) {
    __extends(ParticlesOptimization, _super);
    function ParticlesOptimization() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @return description string
     */
    ParticlesOptimization.prototype.getDescription = function () {
        return "Turning particles on/off";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    ParticlesOptimization.prototype.apply = function (scene, optimizer) {
        scene.particlesEnabled = optimizer.isInImprovementMode;
        return true;
    };
    return ParticlesOptimization;
}(SceneOptimization));
export { ParticlesOptimization };
/**
 * Defines an optimization used to turn render targets off
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var RenderTargetsOptimization = /** @class */ (function (_super) {
    __extends(RenderTargetsOptimization, _super);
    function RenderTargetsOptimization() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Gets a string describing the action executed by the current optimization
     * @return description string
     */
    RenderTargetsOptimization.prototype.getDescription = function () {
        return "Turning render targets off";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @returns true if everything that can be done was applied
     */
    RenderTargetsOptimization.prototype.apply = function (scene, optimizer) {
        scene.renderTargetsEnabled = optimizer.isInImprovementMode;
        return true;
    };
    return RenderTargetsOptimization;
}(SceneOptimization));
export { RenderTargetsOptimization };
/**
 * Defines an optimization used to merge meshes with compatible materials
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var MergeMeshesOptimization = /** @class */ (function (_super) {
    __extends(MergeMeshesOptimization, _super);
    function MergeMeshesOptimization() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._canBeMerged = function (abstractMesh) {
            if (!(abstractMesh instanceof Mesh)) {
                return false;
            }
            var mesh = abstractMesh;
            if (mesh.isDisposed()) {
                return false;
            }
            if (!mesh.isVisible || !mesh.isEnabled()) {
                return false;
            }
            if (mesh.instances.length > 0) {
                return false;
            }
            if (mesh.skeleton || mesh.hasLODLevels) {
                return false;
            }
            return true;
        };
        return _this;
    }
    Object.defineProperty(MergeMeshesOptimization, "UpdateSelectionTree", {
        /**
         * Gets or sets a boolean which defines if optimization octree has to be updated
         */
        get: function () {
            return MergeMeshesOptimization._UpdateSelectionTree;
        },
        /**
         * Gets or sets a boolean which defines if optimization octree has to be updated
         */
        set: function (value) {
            MergeMeshesOptimization._UpdateSelectionTree = value;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Gets a string describing the action executed by the current optimization
     * @return description string
     */
    MergeMeshesOptimization.prototype.getDescription = function () {
        return "Merging similar meshes together";
    };
    /**
     * This function will be called by the SceneOptimizer when its priority is reached in order to apply the change required by the current optimization
     * @param scene defines the current scene where to apply this optimization
     * @param optimizer defines the current optimizer
     * @param updateSelectionTree defines that the selection octree has to be updated (false by default)
     * @returns true if everything that can be done was applied
     */
    MergeMeshesOptimization.prototype.apply = function (scene, optimizer, updateSelectionTree) {
        var globalPool = scene.meshes.slice(0);
        var globalLength = globalPool.length;
        for (var index = 0; index < globalLength; index++) {
            var currentPool = new Array();
            var current = globalPool[index];
            // Checks
            if (!this._canBeMerged(current)) {
                continue;
            }
            currentPool.push(current);
            // Find compatible meshes
            for (var subIndex = index + 1; subIndex < globalLength; subIndex++) {
                var otherMesh = globalPool[subIndex];
                if (!this._canBeMerged(otherMesh)) {
                    continue;
                }
                if (otherMesh.material !== current.material) {
                    continue;
                }
                if (otherMesh.checkCollisions !== current.checkCollisions) {
                    continue;
                }
                currentPool.push(otherMesh);
                globalLength--;
                globalPool.splice(subIndex, 1);
                subIndex--;
            }
            if (currentPool.length < 2) {
                continue;
            }
            // Merge meshes
            Mesh.MergeMeshes(currentPool, undefined, true);
        }
        // Call the octree system optimization if it is defined.
        var sceneAsAny = scene;
        if (sceneAsAny.createOrUpdateSelectionOctree) {
            if (updateSelectionTree != undefined) {
                if (updateSelectionTree) {
                    sceneAsAny.createOrUpdateSelectionOctree();
                }
            }
            else if (MergeMeshesOptimization.UpdateSelectionTree) {
                sceneAsAny.createOrUpdateSelectionOctree();
            }
        }
        return true;
    };
    MergeMeshesOptimization._UpdateSelectionTree = false;
    return MergeMeshesOptimization;
}(SceneOptimization));
export { MergeMeshesOptimization };
/**
 * Defines a list of options used by SceneOptimizer
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var SceneOptimizerOptions = /** @class */ (function () {
    /**
     * Creates a new list of options used by SceneOptimizer
     * @param targetFrameRate defines the target frame rate to reach (60 by default)
     * @param trackerDuration defines the interval between two checks (2000ms by default)
     */
    function SceneOptimizerOptions(
    /**
     * Defines the target frame rate to reach (60 by default)
     */
    targetFrameRate, 
    /**
     * Defines the interval between two checks (2000ms by default)
     */
    trackerDuration) {
        if (targetFrameRate === void 0) { targetFrameRate = 60; }
        if (trackerDuration === void 0) { trackerDuration = 2000; }
        this.targetFrameRate = targetFrameRate;
        this.trackerDuration = trackerDuration;
        /**
         * Gets the list of optimizations to apply
         */
        this.optimizations = new Array();
    }
    /**
     * Add a new optimization
     * @param optimization defines the SceneOptimization to add to the list of active optimizations
     * @returns the current SceneOptimizerOptions
     */
    SceneOptimizerOptions.prototype.addOptimization = function (optimization) {
        this.optimizations.push(optimization);
        return this;
    };
    /**
     * Add a new custom optimization
     * @param onApply defines the callback called to apply the custom optimization (true if everything that can be done was applied)
     * @param onGetDescription defines the callback called to get the description attached with the optimization.
     * @param priority defines the priority of this optimization (0 by default which means first in the list)
     * @returns the current SceneOptimizerOptions
     */
    SceneOptimizerOptions.prototype.addCustomOptimization = function (onApply, onGetDescription, priority) {
        if (priority === void 0) { priority = 0; }
        var optimization = new CustomOptimization(priority);
        optimization.onApply = onApply;
        optimization.onGetDescription = onGetDescription;
        this.optimizations.push(optimization);
        return this;
    };
    /**
     * Creates a list of pre-defined optimizations aimed to reduce the visual impact on the scene
     * @param targetFrameRate defines the target frame rate (60 by default)
     * @returns a SceneOptimizerOptions object
     */
    SceneOptimizerOptions.LowDegradationAllowed = function (targetFrameRate) {
        var result = new SceneOptimizerOptions(targetFrameRate);
        var priority = 0;
        result.addOptimization(new MergeMeshesOptimization(priority));
        result.addOptimization(new ShadowsOptimization(priority));
        result.addOptimization(new LensFlaresOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new PostProcessesOptimization(priority));
        result.addOptimization(new ParticlesOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new TextureOptimization(priority, 1024));
        return result;
    };
    /**
     * Creates a list of pre-defined optimizations aimed to have a moderate impact on the scene visual
     * @param targetFrameRate defines the target frame rate (60 by default)
     * @returns a SceneOptimizerOptions object
     */
    SceneOptimizerOptions.ModerateDegradationAllowed = function (targetFrameRate) {
        var result = new SceneOptimizerOptions(targetFrameRate);
        var priority = 0;
        result.addOptimization(new MergeMeshesOptimization(priority));
        result.addOptimization(new ShadowsOptimization(priority));
        result.addOptimization(new LensFlaresOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new PostProcessesOptimization(priority));
        result.addOptimization(new ParticlesOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new TextureOptimization(priority, 512));
        // Next priority
        priority++;
        result.addOptimization(new RenderTargetsOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new HardwareScalingOptimization(priority, 2));
        return result;
    };
    /**
     * Creates a list of pre-defined optimizations aimed to have a big impact on the scene visual
     * @param targetFrameRate defines the target frame rate (60 by default)
     * @returns a SceneOptimizerOptions object
     */
    SceneOptimizerOptions.HighDegradationAllowed = function (targetFrameRate) {
        var result = new SceneOptimizerOptions(targetFrameRate);
        var priority = 0;
        result.addOptimization(new MergeMeshesOptimization(priority));
        result.addOptimization(new ShadowsOptimization(priority));
        result.addOptimization(new LensFlaresOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new PostProcessesOptimization(priority));
        result.addOptimization(new ParticlesOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new TextureOptimization(priority, 256));
        // Next priority
        priority++;
        result.addOptimization(new RenderTargetsOptimization(priority));
        // Next priority
        priority++;
        result.addOptimization(new HardwareScalingOptimization(priority, 4));
        return result;
    };
    return SceneOptimizerOptions;
}());
export { SceneOptimizerOptions };
/**
 * Class used to run optimizations in order to reach a target frame rate
 * @description More details at https://doc.babylonjs.com/how_to/how_to_use_sceneoptimizer
 */
var SceneOptimizer = /** @class */ (function () {
    /**
     * Creates a new SceneOptimizer
     * @param scene defines the scene to work on
     * @param options defines the options to use with the SceneOptimizer
     * @param autoGeneratePriorities defines if priorities must be generated and not read from SceneOptimization property (true by default)
     * @param improvementMode defines if the scene optimizer must run the maximum optimization while staying over a target frame instead of trying to reach the target framerate (false by default)
     */
    function SceneOptimizer(scene, options, autoGeneratePriorities, improvementMode) {
        if (autoGeneratePriorities === void 0) { autoGeneratePriorities = true; }
        if (improvementMode === void 0) { improvementMode = false; }
        var _this = this;
        this._isRunning = false;
        this._currentPriorityLevel = 0;
        this._targetFrameRate = 60;
        this._trackerDuration = 2000;
        this._currentFrameRate = 0;
        this._improvementMode = false;
        /**
         * Defines an observable called when the optimizer reaches the target frame rate
         */
        this.onSuccessObservable = new Observable();
        /**
         * Defines an observable called when the optimizer enables an optimization
         */
        this.onNewOptimizationAppliedObservable = new Observable();
        /**
         * Defines an observable called when the optimizer is not able to reach the target frame rate
         */
        this.onFailureObservable = new Observable();
        if (!options) {
            this._options = new SceneOptimizerOptions();
        }
        else {
            this._options = options;
        }
        if (this._options.targetFrameRate) {
            this._targetFrameRate = this._options.targetFrameRate;
        }
        if (this._options.trackerDuration) {
            this._trackerDuration = this._options.trackerDuration;
        }
        if (autoGeneratePriorities) {
            var priority = 0;
            for (var _i = 0, _a = this._options.optimizations; _i < _a.length; _i++) {
                var optim = _a[_i];
                optim.priority = priority++;
            }
        }
        this._improvementMode = improvementMode;
        this._scene = scene || EngineStore.LastCreatedScene;
        this._sceneDisposeObserver = this._scene.onDisposeObservable.add(function () {
            _this._sceneDisposeObserver = null;
            _this.dispose();
        });
    }
    Object.defineProperty(SceneOptimizer.prototype, "isInImprovementMode", {
        /**
         * Gets or sets a boolean indicating if the optimizer is in improvement mode
         */
        get: function () {
            return this._improvementMode;
        },
        set: function (value) {
            this._improvementMode = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneOptimizer.prototype, "currentPriorityLevel", {
        /**
         * Gets the current priority level (0 at start)
         */
        get: function () {
            return this._currentPriorityLevel;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneOptimizer.prototype, "currentFrameRate", {
        /**
         * Gets the current frame rate checked by the SceneOptimizer
         */
        get: function () {
            return this._currentFrameRate;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneOptimizer.prototype, "targetFrameRate", {
        /**
         * Gets or sets the current target frame rate (60 by default)
         */
        get: function () {
            return this._targetFrameRate;
        },
        /**
         * Gets or sets the current target frame rate (60 by default)
         */
        set: function (value) {
            this._targetFrameRate = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneOptimizer.prototype, "trackerDuration", {
        /**
         * Gets or sets the current interval between two checks (every 2000ms by default)
         */
        get: function () {
            return this._trackerDuration;
        },
        /**
         * Gets or sets the current interval between two checks (every 2000ms by default)
         */
        set: function (value) {
            this._trackerDuration = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SceneOptimizer.prototype, "optimizations", {
        /**
         * Gets the list of active optimizations
         */
        get: function () {
            return this._options.optimizations;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Stops the current optimizer
     */
    SceneOptimizer.prototype.stop = function () {
        this._isRunning = false;
    };
    /**
     * Reset the optimizer to initial step (current priority level = 0)
     */
    SceneOptimizer.prototype.reset = function () {
        this._currentPriorityLevel = 0;
    };
    /**
     * Start the optimizer. By default it will try to reach a specific framerate
     * but if the optimizer is set with improvementMode === true then it will run all optimization while frame rate is above the target frame rate
     */
    SceneOptimizer.prototype.start = function () {
        var _this = this;
        if (this._isRunning) {
            return;
        }
        this._isRunning = true;
        // Let's wait for the scene to be ready before running our check
        this._scene.executeWhenReady(function () {
            setTimeout(function () {
                _this._checkCurrentState();
            }, _this._trackerDuration);
        });
    };
    SceneOptimizer.prototype._checkCurrentState = function () {
        var _this = this;
        if (!this._isRunning) {
            return;
        }
        var scene = this._scene;
        var options = this._options;
        this._currentFrameRate = Math.round(scene.getEngine().getFps());
        if ((this._improvementMode && this._currentFrameRate <= this._targetFrameRate) || (!this._improvementMode && this._currentFrameRate >= this._targetFrameRate)) {
            this._isRunning = false;
            this.onSuccessObservable.notifyObservers(this);
            return;
        }
        // Apply current level of optimizations
        var allDone = true;
        var noOptimizationApplied = true;
        for (var index = 0; index < options.optimizations.length; index++) {
            var optimization = options.optimizations[index];
            if (optimization.priority === this._currentPriorityLevel) {
                noOptimizationApplied = false;
                allDone = allDone && optimization.apply(scene, this);
                this.onNewOptimizationAppliedObservable.notifyObservers(optimization);
            }
        }
        // If no optimization was applied, this is a failure :(
        if (noOptimizationApplied) {
            this._isRunning = false;
            this.onFailureObservable.notifyObservers(this);
            return;
        }
        // If all optimizations were done, move to next level
        if (allDone) {
            this._currentPriorityLevel++;
        }
        // Let's the system running for a specific amount of time before checking FPS
        scene.executeWhenReady(function () {
            setTimeout(function () {
                _this._checkCurrentState();
            }, _this._trackerDuration);
        });
    };
    /**
     * Release all resources
     */
    SceneOptimizer.prototype.dispose = function () {
        this.stop();
        this.onSuccessObservable.clear();
        this.onFailureObservable.clear();
        this.onNewOptimizationAppliedObservable.clear();
        if (this._sceneDisposeObserver) {
            this._scene.onDisposeObservable.remove(this._sceneDisposeObserver);
        }
    };
    /**
     * Helper function to create a SceneOptimizer with one single line of code
     * @param scene defines the scene to work on
     * @param options defines the options to use with the SceneOptimizer
     * @param onSuccess defines a callback to call on success
     * @param onFailure defines a callback to call on failure
     * @returns the new SceneOptimizer object
     */
    SceneOptimizer.OptimizeAsync = function (scene, options, onSuccess, onFailure) {
        var optimizer = new SceneOptimizer(scene, options || SceneOptimizerOptions.ModerateDegradationAllowed(), false);
        if (onSuccess) {
            optimizer.onSuccessObservable.add(function () {
                onSuccess();
            });
        }
        if (onFailure) {
            optimizer.onFailureObservable.add(function () {
                onFailure();
            });
        }
        optimizer.start();
        return optimizer;
    };
    return SceneOptimizer;
}());
export { SceneOptimizer };
//# sourceMappingURL=sceneOptimizer.js.map